/**
 * T-0896 (LC-6i): the desktop launch's own steps, for the gate's launch smoke (RES-1) to show the launch
 * unchanged when its modules move: error forwarding on, "Restoring your session…" painted, the session
 * restored, the language sent. Each step notes itself here with its time since navigation start;
 * `launchSmokeReporter.ts` reports them in the launch-smoke build only, those noted before it started
 * included. In every other build a note is a few bytes held in memory.
 *
 * No imports: the modules that note a step are in the first-screen entry, and this is all they bring.
 */
export interface ILaunchMilestone {
  name: string;
  /** Milliseconds since navigation start (`performance.now()`), rounded. */
  atMs: number;
}

/** Notes beyond this many are dropped: a launch takes a handful. */
export const LAUNCH_MILESTONES_HELD = 20;

const noted: ILaunchMilestone[] = [];
const listeners = new Set<(milestone: ILaunchMilestone) => void>();

/** A name longer than this is cut: the app takes a page milestone of at most 160 characters. */
const NAME_CHARS = 120;

/** A milestone's line: its name and its time, in the characters a page milestone may hold. */
export function launchMilestoneLine({ name, atMs }: ILaunchMilestone): string {
  return `${name.slice(0, NAME_CHARS)} at ${atMs} ms`.replace(/[^A-Za-z0-9 :._-]/g, '-');
}

/** Notes one step of the launch, now. */
export function noteLaunchMilestone(
  name: string,
  now: () => number = () => performance.now(),
): void {
  if (noted.length >= LAUNCH_MILESTONES_HELD) return;
  const milestone = { name, atMs: Math.round(now()) };
  noted.push(milestone);
  listeners.forEach((listener) => listener(milestone));
}

/** Hears every step, those noted already first. Answers the function that stops. */
export function onLaunchMilestone(listener: (milestone: ILaunchMilestone) => void): () => void {
  noted.forEach(listener);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** For tests: forget every note and listener. */
export function resetLaunchMilestonesForTests(): void {
  noted.splice(0);
  listeners.clear();
}
